import { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { currencyForCountry, formatMoney, parseAmountToCents, validateSubscriptionForm } from "@/lib/retailerInvoice";

interface RetailerOption {
  id:            string;
  store_name:    string | null;
  email:         string | null;
  contact_email: string | null;
  city:          string | null;
  state:         string | null;
  country:       string | null;
  status:        string | null;
  is_demo:       boolean | null;
}

interface Agreement {
  id: string;
  stripe_subscription_id: string | null;
  subscription_status: string | null;
  invoice_status: string;
  invoice_url: string | null;
  currency: string;
  quantity: number | null;
  unit_amount_cents: number | null;
  amount_cents: number;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  scheduled_unit_amount_cents: number | null;
  scheduled_change_at: string | null;
  order_form_ref: string | null;
}

interface SuccessResult {
  subscription_id: string; invoice_id: string | null; invoice_url: string | null; customer_id: string;
  agreement_recorded: boolean; activated: boolean; email: string; retailer: string; currency: string; total_cents: number;
}

const todayIso = () => new Date().toISOString().slice(0, 10);
const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" }) : "—");

export default function AdminSendInvoice() {
  const { session } = useAuth();
  const [unitAmount, setUnitAmount]   = useState("");
  const [locations, setLocations]     = useState("1");
  const [startDate, setStartDate]     = useState(todayIso());
  const [isFounding, setIsFounding]   = useState(false);
  const [orderFormRef, setOrderFormRef] = useState("");
  const [requestId, setRequestId]     = useState(() => crypto.randomUUID());
  const [sending, setSending]         = useState(false);
  const [error,   setError]           = useState<string | null>(null);
  const [result,  setResult]          = useState<SuccessResult | null>(null);
  const [copied,  setCopied]          = useState(false);

  // ── Retailer picker ────────────────────────────────────────────────
  const [query,   setQuery]   = useState("");
  const [options, setOptions] = useState<RetailerOption[]>([]);
  const [picked,  setPicked]  = useState<RetailerOption | null>(null);
  const [agreement, setAgreement] = useState<Agreement | null>(null);

  useEffect(() => {
    if (picked) return;
    const t = setTimeout(async () => {
      const q = query.trim().replace(/[%,()]/g, "");
      let req = supabase
        .from("retailers")
        .select("id, store_name, email, contact_email, city, state, country, status, is_demo")
        .order("created_at", { ascending: false })
        .limit(8);
      if (q) req = req.or(`store_name.ilike.%${q}%,email.ilike.%${q}%,contact_email.ilike.%${q}%`);
      const { data } = await req;
      setOptions((data as unknown as RetailerOption[]) ?? []);
    }, 250);
    return () => clearTimeout(t);
  }, [query, picked]);

  const loadAgreement = useCallback(async (retailerId: string) => {
    const { data } = await supabase
      .from("retailer_agreements")
      .select("id, stripe_subscription_id, subscription_status, invoice_status, invoice_url, currency, quantity, unit_amount_cents, amount_cents, current_period_end, cancel_at_period_end, scheduled_unit_amount_cents, scheduled_change_at, order_form_ref")
      .eq("retailer_id", retailerId)
      .not("stripe_subscription_id", "is", null)
      .order("created_at", { ascending: false })
      .limit(1);
    setAgreement(((data as unknown as Agreement[]) ?? [])[0] ?? null);
  }, []);

  function pickRetailer(r: RetailerOption) {
    setPicked(r);
    setError(null);
    void loadAgreement(r.id);
  }

  const currency = picked ? currencyForCountry(picked.country) : null;
  const unitCents = useMemo(() => parseAmountToCents(unitAmount), [unitAmount]);
  const qty = Number(locations);
  const total = unitCents !== null && Number.isInteger(qty) && qty > 0 ? unitCents * qty : null;
  const recipient = picked ? (picked.contact_email || picked.email || "") : "";
  const liveSubscription = agreement && agreement.subscription_status && !["canceled", "incomplete_expired"].includes(agreement.subscription_status);

  // ── Create ─────────────────────────────────────────────────────────
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!session?.access_token) { setError("Not authenticated."); return; }
    const problem = validateSubscriptionForm({
      retailerId: picked?.id ?? null, retailerIsDemo: picked?.is_demo === true, retailerCountry: picked?.country ?? null,
      unitAmount, locations, startDate, todayIso: todayIso(),
    });
    if (problem) { setError(problem); return; }
    if (!recipient) { setError("This retailer has no email on file. Add one in Retailers first."); return; }

    const label = `${qty} location${qty === 1 ? "" : "s"} × ${formatMoney(unitCents!, currency!)} a year (${formatMoney(total!, currency!)} before tax) for ${picked!.store_name ?? recipient}`;
    if (!window.confirm(`Create this subscription and send the first invoice?\n\n${label}\nStarts ${startDate}. Renews yearly. Stripe emails ${recipient}.`)) return;

    setSending(true); setError(null); setResult(null);
    const { data, error: fnErr } = await supabase.functions.invoke("create-retailer-subscription", {
      body: {
        retailer_id: picked!.id, unit_amount_cents: unitCents, quantity: qty, start_date: startDate,
        is_founding: isFounding, order_form_ref: orderFormRef.trim() || undefined,
        request_id: requestId,
      },
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    setSending(false);
    if (fnErr || data?.error) { setError(fnErr?.message ?? data?.error ?? "Unknown error"); return; }

    setResult({
      subscription_id: data.subscription_id, invoice_id: data.invoice_id, invoice_url: data.invoice_url, customer_id: data.customer_id,
      agreement_recorded: data.agreement_recorded !== false, activated: data.activated === true,
      email: recipient, retailer: picked!.store_name ?? recipient, currency: data.currency, total_cents: data.total_cents,
    });
    void loadAgreement(picked!.id);
    setUnitAmount(""); setLocations("1"); setOrderFormRef(""); setStartDate(todayIso());
    setRequestId(crypto.randomUUID());
  }

  // ── Manage a live subscription ─────────────────────────────────────
  const [newLocations, setNewLocations] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  async function manage(body: Record<string, unknown>, confirmText: string) {
    if (!session?.access_token || !picked) return;
    if (!window.confirm(confirmText)) return;
    setBusy(true); setNote(null); setError(null);
    const { data, error: fnErr } = await supabase.functions.invoke("manage-retailer-subscription", {
      body: { retailer_id: picked.id, ...body },
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    setBusy(false);
    if (fnErr || data?.error) { setError(fnErr?.message ?? data?.error ?? "Unknown error"); return; }
    setNote("Done. Stripe has the change; the portal updates when Stripe confirms it (a few seconds).");
    setNewLocations(""); setNewPrice("");
    setTimeout(() => void loadAgreement(picked.id), 2500);
  }

  async function copyUrl(url: string) {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* clipboard unavailable */ }
  }

  return (
    <div className="space-y-6 max-w-xl">
      <div>
        <h1 className="text-2xl font-semibold text-white">Create subscription and send invoice</h1>
        <p className="mt-1 text-sm text-gray-400">
          Starts an annual, auto-renewing Stripe subscription for the retailer (price per location × number of
          locations). Stripe emails the first invoice with a payment link, adds GST/HST/QST automatically, and
          sends each renewal invoice on the renewal date with 30 days to pay. The currency follows the retailer's country. Portal
          access is switched on straight away; any suspension for late payment is a manual step.
        </p>
      </div>

      {result && (
        <div className="rounded-xl border border-green-900/40 bg-green-900/15 p-5 space-y-4">
          <p className="font-medium text-green-400">✓ Subscription created for {result.retailer} ({formatMoney(result.total_cents, result.currency)} + tax a year)</p>
          {result.activated ? (
            <p className="text-xs text-green-400/80">Portal access is now active for this retailer.</p>
          ) : (
            <p className="text-xs text-yellow-400">Portal access could not be switched on. Use "Activate without payment" on the retailer's panel.</p>
          )}
          {!result.agreement_recorded && (
            <p className="text-xs text-yellow-400">The subscription exists in Stripe but the agreement record could not be saved; the next Stripe event will create it.</p>
          )}
          {result.invoice_url ? (
            <div className="space-y-2">
              <p className="text-xs text-gray-500">First invoice URL (click to copy), emailed to {result.email}</p>
              <button type="button" onClick={() => copyUrl(result.invoice_url!)}
                className="w-full rounded-lg border border-gray-700 bg-gray-900/60 px-3 py-2.5 text-left text-xs text-gray-300 break-all hover:bg-gray-800 transition">
                {result.invoice_url}
              </button>
              {copied && <p className="text-xs text-green-400">Copied to clipboard ✓</p>}
            </div>
          ) : (
            <p className="text-xs text-gray-400">No invoice yet: the start date is in the future, so Stripe invoices on that date.</p>
          )}
          <div className="flex gap-2 text-xs text-gray-600"><span>Subscription: {result.subscription_id}</span><span>·</span><span>Customer: {result.customer_id}</span></div>
          <button type="button" onClick={() => setResult(null)} className="text-xs text-gray-500 hover:text-gray-300 underline underline-offset-2 transition">Close</button>
        </div>
      )}

      {error && <div className="rounded-xl border border-red-900/40 bg-red-900/20 px-4 py-3 text-sm text-red-400">{error}</div>}
      {note && <div className="rounded-xl border border-green-900/40 bg-green-900/15 px-4 py-3 text-sm text-green-400">{note}</div>}

      <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-6 space-y-5">
        <Field label="Retailer *" htmlFor="retailer_search">
          {picked ? (
            <div className="flex items-start justify-between gap-3 rounded-lg border border-gray-700 bg-gray-800/60 px-3 py-2.5">
              <div className="text-sm">
                <p className="text-gray-100 font-medium">{picked.store_name ?? "(no name)"}{picked.is_demo ? " · Demo" : ""}</p>
                <p className="text-xs text-gray-500">
                  {recipient || "no email on file"}
                  {picked.city || picked.state ? ` · ${[picked.city, picked.state].filter(Boolean).join(", ")}` : ""}
                  {picked.country ? ` · ${picked.country}` : ""}{currency ? ` · billed in ${currency}` : ""}
                </p>
              </div>
              <button type="button" onClick={() => { setPicked(null); setAgreement(null); setNote(null); }}
                className="text-xs text-gray-400 underline underline-offset-2 hover:text-gray-200">Change</button>
            </div>
          ) : (
            <>
              <input id="retailer_search" type="text" autoComplete="off" placeholder="Search by store name or email"
                value={query} onChange={(e) => setQuery(e.target.value)} className={inputCls} />
              <ul className="mt-2 divide-y divide-gray-800 rounded-lg border border-gray-800">
                {options.length === 0 && <li className="px-3 py-2 text-xs text-gray-600">No matching retailers.</li>}
                {options.map((o) => (
                  <li key={o.id}>
                    <button type="button" onClick={() => pickRetailer(o)} className="w-full px-3 py-2 text-left text-sm text-gray-200 hover:bg-gray-800">
                      {o.store_name ?? "(no name)"}{o.is_demo ? " · Demo" : ""}
                      <span className="ml-2 text-xs text-gray-500">{o.contact_email || o.email || "no email"} · {o.status ?? "—"}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Field>

        {/* Existing subscription */}
        {picked && agreement && (
          <div className="rounded-lg border border-gray-700 bg-gray-800/50 px-4 py-3 space-y-3">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Current subscription</p>
            <p className="text-sm text-gray-200">
              {agreement.quantity ?? "?"} location{agreement.quantity === 1 ? "" : "s"} × {agreement.unit_amount_cents ? formatMoney(agreement.unit_amount_cents, agreement.currency) : "—"} a year
              <span className="ml-2 text-xs text-gray-400">({agreement.subscription_status ?? "—"}, last invoice {agreement.invoice_status})</span>
            </p>
            <p className="text-xs text-gray-400">
              {agreement.cancel_at_period_end
                ? `Cancels on ${fmtDate(agreement.current_period_end)}; no renewal.`
                : `Next renewal ${fmtDate(agreement.current_period_end)}: ${formatMoney((agreement.scheduled_unit_amount_cents ?? agreement.unit_amount_cents ?? 0) * (agreement.quantity ?? 1), agreement.currency)} + tax`}
              {agreement.scheduled_unit_amount_cents ? ` (new price ${formatMoney(agreement.scheduled_unit_amount_cents, agreement.currency)} per location, scheduled)` : ""}
            </p>
            {liveSubscription && (
              <div className="space-y-3 border-t border-gray-700 pt-3">
                <div className="flex gap-2">
                  <input aria-label="New number of locations" type="text" inputMode="numeric" placeholder="New locations" value={newLocations}
                    onChange={(e) => setNewLocations(e.target.value)} className={inputCls} />
                  <button type="button" disabled={busy || !/^\d+$/.test(newLocations)} onClick={() => manage({ action: "set_quantity", quantity: Number(newLocations) },
                    `Change to ${newLocations} locations? Stripe prorates the change onto the next invoice.`)} className={btnCls}>Update locations</button>
                </div>
                <div className="flex gap-2">
                  <input aria-label="New price per location" type="text" inputMode="decimal" placeholder="New annual price per location" value={newPrice}
                    onChange={(e) => setNewPrice(e.target.value)} className={inputCls} />
                  <button type="button" disabled={busy || parseAmountToCents(newPrice) === null} onClick={() => manage({ action: "schedule_price", unit_amount_cents: parseAmountToCents(newPrice) },
                    `Schedule ${newPrice} per location at the next renewal? It needs at least 90 days' notice to the retailer.`)} className={btnCls}>Schedule price</button>
                </div>
                <button type="button" disabled={busy} onClick={() => manage({ action: agreement.cancel_at_period_end ? "resume" : "cancel_at_period_end" },
                  agreement.cancel_at_period_end ? "Resume auto-renewal?" : "Cancel at the end of the paid period? The retailer keeps access until then.")}
                  className="text-xs text-gray-400 underline underline-offset-2 hover:text-gray-200 disabled:opacity-40">
                  {agreement.cancel_at_period_end ? "Resume auto-renewal" : "Cancel at period end"}
                </button>
              </div>
            )}
          </div>
        )}

        {/* New subscription */}
        {!liveSubscription && (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <Field label={`Annual price per location${currency ? ` (${currency})` : ""} *`} htmlFor="unit_amount">
                <input id="unit_amount" type="text" inputMode="decimal" placeholder="e.g. 1200" value={unitAmount}
                  onChange={(e) => { setUnitAmount(e.target.value); setError(null); }} className={inputCls} />
              </Field>
              <Field label="Locations *" htmlFor="locations">
                <input id="locations" type="text" inputMode="numeric" value={locations}
                  onChange={(e) => { setLocations(e.target.value); setError(null); }} className={inputCls} />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Start date *" htmlFor="start_date">
                <input id="start_date" type="date" min={todayIso()} value={startDate} onChange={(e) => { setStartDate(e.target.value); setError(null); }} className={inputCls} />
              </Field>
              <Field label="Order Form reference" htmlFor="order_form_ref">
                <input id="order_form_ref" type="text" maxLength={100} placeholder="optional" value={orderFormRef} onChange={(e) => setOrderFormRef(e.target.value)} className={inputCls} />
              </Field>
            </div>
            <label className="flex items-start gap-3 text-sm text-gray-300">
              <input type="checkbox" checked={isFounding} onChange={(e) => setIsFounding(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-600 bg-gray-800" />
              <span>Founding retailer<span className="block text-xs text-gray-500">Marks the retailer as part of the Founding retailer program. No spot limit.</span></span>
            </label>
            <div className="rounded-lg border border-gray-700 bg-gray-800/50 px-4 py-3 space-y-1">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Subscription</p>
              <p className="text-sm text-gray-300">
                {Number.isInteger(qty) && qty > 0 ? qty : "—"} location{qty === 1 ? "" : "s"} × {unitCents !== null && currency ? formatMoney(unitCents, currency) : "—"}
                <span className="ml-2 text-white font-semibold">{total !== null && currency ? `${formatMoney(total, currency)} + tax` : "—"}</span> a year
              </p>
              <p className="text-xs text-gray-500">Starts {startDate || "—"} · renews yearly · 30 days to pay · Stripe sends the invoice email</p>
            </div>
            <button type="submit" disabled={sending || !picked}
              className="w-full rounded-lg bg-orange-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-40 transition">
              {sending ? "Creating…" : "Create subscription and send invoice"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

/* ── Small helpers ──────────────────────────────────────────────────── */

const inputCls =
  "w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 " +
  "placeholder-gray-600 outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition";
const btnCls = "shrink-0 rounded-lg border border-gray-600 px-3 py-2 text-xs font-medium text-gray-200 hover:bg-gray-700 disabled:opacity-40 transition";

function Field({ label, htmlFor, children }: { label: string; htmlFor: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="block text-xs font-medium text-gray-400">{label}</label>
      {children}
    </div>
  );
}
